import { ReactQueryStaleTime } from '@openx/constants/react-query.ts';
import { Order_By, RetargetingSegmentStatus } from '@openx/types/gqlTypes.ts';
import type { UseRetargetingSegmentsOptionsFetch } from '@openx/types/targeting/targetingHookTypes.ts';
import { QueryKeys } from '@openx/utils/api/queryKeys.ts';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { RetargetingSegmentsDocument } from '../../../graphql/query/segments/GetRetargetingSegments.generated.tsx';
import { getGraphQLRequestFromClient } from '../../../modules/graphql/types.ts';
import { mapEligibleRetargetingSegments } from './mapEligibleRetargetingSegments.ts';

const RETARGETING_SEGMENTS_LIMIT = 500;

export const useRetargetingSegmentsOptionsFetch: UseRetargetingSegmentsOptionsFetch = ({
  accountUid,
  disposition = '',
  enabled = true,
}) => {
  const shouldFetch = enabled && Boolean(accountUid) && Boolean(disposition);

  const { data, isLoading, refetch } = useQuery({
    enabled: shouldFetch,
    queryFn: ({ client }) =>
      // accountUid is exchange account_uid; routedSegments.account_id expects OA uuid — filter client-side.
      getGraphQLRequestFromClient(client)(RetargetingSegmentsDocument, {
        limit: RETARGETING_SEGMENTS_LIMIT,
        offset: 0,
        where: {
          status: { _eq: RetargetingSegmentStatus.Active },
        },
        orderBy: [{ name: Order_By.Asc }],
      }),
    queryKey: [QueryKeys.RETARGETING_SEGMENTS, accountUid],
    staleTime: ReactQueryStaleTime.MINUTES_15,
  });

  const { segments, segmentsList } = useMemo(
    () =>
      shouldFetch
        ? mapEligibleRetargetingSegments({
            routedSegments: data?.routedSegments ?? [],
            accountUid,
            disposition,
          })
        : { segments: {}, segmentsList: [] },
    [accountUid, data?.routedSegments, disposition, shouldFetch]
  );

  return {
    isLoading: shouldFetch && isLoading,
    segments,
    segmentsList,
    refetch,
  };
};
